import api from "../client";
import type { ScreenerItem, Filter } from "../../../types/screener";
import { frontendTypeToApiOperation } from "@/components/screener/filterTypes";

export type FilterLogic = "and" | "or" | "xor" | "not";

export interface FilterExpressionPayload {
  type: "group";
  id: string;
  logic: FilterLogic;
  children: Array<
    | {
        type: "condition";
        id: string;
        column_id: string;
        operation: string;
        value: unknown;
        negated?: boolean;
      }
    | FilterExpressionPayload
  >;
}

export interface ScreenerSearchParams {
  columns: string[];
  filters: Array<{
    column_id: string;
    operation: string;
    value: any;
    negated?: boolean;
  }>;
  filter_logic?: FilterLogic;
  filter_expression?: FilterExpressionPayload;
  sort_by?: string;
  sort_order?: "asc" | "desc";
  page: number;
  page_size: number;
  search_term?: string;
  use_vector_search?: boolean;
  watchlist_id?: string;
  portfolio_lookthrough_id?: string;
  show_benchmarks?: boolean;
  as_at?: string; // Optional as_at date (YYYY-MM-DD format)
}

export interface ScreenerSearchResponse {
  items: ScreenerItem[];
  pagination: {
    page: number;
    page_size: number;
    total_items: number;
    total_pages: number;
  };
  applied_filters: Array<{
    column_id: string;
    operation: string;
    value: string;
  }>;
  columns: Array<{
    id: string;
    name: string;
    description: string;
  }>;
}

export interface FilterConfig {
  allowed_values?: (string | number | boolean)[];
  min_value?: number;
  max_value?: number;
  is_percentage?: boolean;
  is_currency?: boolean;
  allow_null?: boolean;
}

export interface ScreenerColumn {
  id: string;
  name: string;
  description?: string;
  type: "string" | "number" | "boolean" | "date";
  data_type?:
  | "string"
  | "number"
  | "boolean"
  | "date"
  | "currency"
  | "percentage"
  | "asset_allocation"
  | "region_allocation"
  | "sector_allocation";
  filterable: boolean;
  sortable: boolean;
  group?: string;
  field_path?: string;
  fallback_path?: string;
  formatting?: {
    type?: "number" | "percentage" | "currency" | "marker";
    options?: {
      style?: "decimal" | "currency";
      currency?: string;
      currencyDisplay?: "symbol" | "narrowSymbol" | "code" | "name";
      minimumFractionDigits?: number;
      maximumFractionDigits?: number;
      locale?: string;
    };
    decimal_places?: number;
    decimals?: number;
    date_format?: string;
    prefix?: string;
    suffix?: string;
    alignment?: "left" | "center" | "right";
  };
  filter_config?: FilterConfig;
}

/**
 * Generate mock columns for testing purposes
 * This is only used for development and testing when the API is not available
 */
export const getMockColumns = (): ScreenerColumn[] => {
  const mockColumns: ScreenerColumn[] = [
    {
      id: "name",
      name: "Name",
      type: "string",
      filterable: true,
      sortable: true,
      description: "The name of the security",
    },
    {
      id: "price",
      name: "Price",
      type: "number",
      filterable: true,
      sortable: true,
      description: "Current price",
    },
    {
      id: "change",
      name: "Change",
      type: "number",
      filterable: true,
      sortable: true,
      description: "Price change",
    },
    {
      id: "volume",
      name: "Volume",
      type: "number",
      filterable: true,
      sortable: true,
      description: "Trading volume",
    },
    {
      id: "marketCap",
      name: "Market Cap",
      type: "number",
      filterable: true,
      sortable: true,
      description: "Market capitalization",
    },
    {
      id: "type",
      name: "Type",
      type: "string",
      filterable: true,
      sortable: true,
      description: "Security type",
    },
  ];

  // Ensure all columns have the filterable property set to true
  mockColumns.forEach((col) => {
    if (col.filterable === undefined) {
      col.filterable = true;
    }
  });

  return mockColumns;
};

/**
 * Fetch screener data with filters
 */
export const fetchScreenerSearch = async (
  searchTerm?: string,
  sortBy: string = "display_name",
  sortOrder: "asc" | "desc" = "asc",
  page: number = 1,
  pageSize: number = 50,
  resourceTypes?: string[],
  options?: { signal?: AbortSignal },
): Promise<ScreenerSearchResponse> => {
  if (!searchTerm || searchTerm.length < 2) {
    return {
      items: [],
      pagination: {
        page: page,
        page_size: pageSize,
        total_items: 0,
        total_pages: 0,
      },
      applied_filters: [],
      columns: [],
    };
  }

  try {
    const filters: any[] = [];

    // Add resource type filter if specified
    if (resourceTypes && resourceTypes.length > 0) {
      filters.push({
        column_id: "resource_type",
        operation: "in",
        value: resourceTypes,
      });
    }

    const request: ScreenerSearchParams = {
      columns: [
        "display_name",
        "resource_type",
        "id",
        "currency",
        "default_stream_type",
        "isin",
        "long_name",
        "total_return_first_real_date",
        // Portfolios use fundamental_data.name; long_name is often empty in snapshot
        "portfolio_name",
      ],
      filters: filters,
      ...(sortBy && { sort_by: sortBy, sort_order: sortOrder }),
      page,
      page_size: pageSize,
      use_vector_search: true,
      search_term: searchTerm,
    };

    const response = await api({
      method: "POST",
      url: "/screener/search",
      data: request,
      signal: options?.signal,
    });

    return response.data;
  } catch (error) {
    console.error("Error fetching screener data:", error);
    throw error;
  }
};

/**
 * Fetch formatting for specific field paths
 */
export const fetchColumnFormatting = async (
  fieldPaths: string[]
): Promise<Record<string, { id: string; name: string; data_type: string; formatting: ScreenerColumn["formatting"] }>> => {
  if (!fieldPaths.length) return {};

  try {
    const response = await api.get<Record<string, any>>(
      `/screener/columns/formatting?field_paths=${fieldPaths.join(",")}`
    );

    if (!response || response.status !== 200) {
      console.error(`API error: ${response?.status} ${response?.statusText}`);
      return {};
    }

    return response.data || {};
  } catch (error) {
    console.error("Error fetching column formatting:", error);
    return {};
  }
};

/**
 * Fetch available columns for the screener
 */
export const fetchScreenerColumns = async (): Promise<ScreenerColumn[]> => {
  try {
    // Use the api client instead of fetch, with the correct endpoint
    const response = await api.get<any>("/screener/columns");

    if (!response || response.status !== 200) {
      console.error(`API error: ${response?.status} ${response?.statusText}`);
      return getMockColumns();
    }

    let columns: ScreenerColumn[] = [];

    // Check if the response is an array or an object with a 'columns' property
    if (Array.isArray(response.data)) {
      columns = response.data;
    } else if (
      response.data &&
      typeof response.data === "object" &&
      Array.isArray(response.data.columns)
    ) {
      columns = response.data.columns;
    } else {
      console.error("Unexpected API response format:", response.data);
      return getMockColumns();
    }

    // Process the columns to ensure they have the correct properties
    columns = columns.map((col) => {
      // Map the API's data_type to our expected type
      let type: "string" | "number" | "boolean" | "date" = "string"; // Default to string

      // Use type assertion to access the data_type property
      const apiColumn = col as any;

      if (
        apiColumn.data_type === "number" ||
        apiColumn.data_type === "integer" ||
        apiColumn.data_type === "currency" ||
        apiColumn.data_type === "percentage"
      ) {
        type = "number";
      } else if (apiColumn.data_type === "boolean") {
        type = "boolean";
      } else if (
        apiColumn.data_type === "date" ||
        apiColumn.data_type === "datetime"
      ) {
        type = "date";
      }

      // Return a properly formatted column object
      return {
        id: col.id,
        name: col.name,
        description: col.description || "",
        type,
        data_type: apiColumn.data_type,
        filterable: true, // Ensure all columns are filterable
        sortable: true, // Ensure all columns are sortable
        group: apiColumn.group || "General",
        field_path: apiColumn.field_path,
        fallback_path: apiColumn.fallback_path,
        formatting: apiColumn.formatting,
        filter_config: apiColumn.filter_config,
      };
    });

    return columns;
  } catch (error) {
    console.error("Error fetching screener columns:", error);
    return getMockColumns();
  }
};

/**
 * Convert frontend filter format to API filter format
 */
export const convertFiltersToApiFormat = (
  filters: Filter[]
): ScreenerSearchParams["filters"] => {
  return filters.map((filter) => ({
    column_id: filter.column,
    operation: frontendTypeToApiOperation(filter.type),
    value: filter.value,
    ...(filter.negated ? { negated: true } : {}),
  }));
};

/**
 * Fetch screener data with filters
 */
export const fetchScreenerData = async (
  filters: Filter[] = [],
  sortBy?: string, // Remove default, allow undefined
  sortOrder: "asc" | "desc" = "asc",
  page: number = 1,
  pageSize: number = 50,
  columns: string[] = [],
  searchTerm?: string,
  watchlistId?: string,
  portfolioLookthroughId?: string,
  showBenchmarks: boolean = false,
  asAt?: string,
  filterLogic: FilterLogic = "and",
  filterExpression?: FilterExpressionPayload,
): Promise<ScreenerSearchResponse> => {
  // Remove watchlist and lookthrough filters as they're handled via top-level parameters
    const filteredFilters = filters.filter(
      (filter) =>
        !(filter.column === "watchlists" && filter.type === "contains") &&
        !filter.column.startsWith("portfolio_lookthrough")
    );

    // When a filter expression tree is sent, flat filters must not duplicate it —
    // the backend prefers filter_expression but legacy paths could still read filters[].
    const apiFilters =
      filterExpression?.children?.length
        ? []
        : convertFiltersToApiFormat(filteredFilters);

    // Ensure columns is always included in the request
    const params: ScreenerSearchParams = {
      columns: columns,
      filters: apiFilters,
      filter_logic: filterLogic,
      page,
      page_size: pageSize,
    };

    if (filterExpression?.children?.length) {
      params.filter_expression = filterExpression;
    }

    // Only include sort parameters if sortBy is set
    if (sortBy) {
      params.sort_by = sortBy;
      params.sort_order = sortOrder;
    }

    // Add watchlist_id if provided
    if (watchlistId) {
      params.watchlist_id = watchlistId;
    }

    // Add portfolio_lookthrough_id if provided
    if (
      portfolioLookthroughId &&
      typeof portfolioLookthroughId === "string" &&
      portfolioLookthroughId.trim() !== ""
    ) {
      params.portfolio_lookthrough_id = portfolioLookthroughId;
    }

    // Add search parameters if a search term is provided
    if (searchTerm) {
      params.search_term = searchTerm;
      params.use_vector_search = true;
    }

    // Add show_benchmarks parameter if true
    if (showBenchmarks) {
      params.show_benchmarks = true;
    }

    // Add as_at parameter if provided
    if (asAt) {
      params.as_at = asAt;
    }

    const response = await api.post<ScreenerSearchResponse>(
      "/screener/search",
      params
    );

    // Transform the response data to match the frontend ScreenerItem format
    const transformedItems = response.data.items.map((item: any) => {
      const metaResourceId = item?.metadata?.resource_id;
      const baseId = metaResourceId || item?.id || item?.data?.id;

      const transformedItem: any = {
        id: baseId || `item-${Math.random().toString(36).substr(2, 9)}`,
        resource_id:
          metaResourceId || item?.resource_id || item?.data?.id || item?.id,
        resource_type: item.resource_type,
        display_name: item.display_name || item?.data?.display_name,
        default_stream_type: item.default_stream_type,
        styling: item.styling,
        is_benchmark_row: item.is_benchmark_row,
      };

      if (item.data && typeof item.data === "object") {
        Object.entries(item.data).forEach(([key, value]) => {
          transformedItem[key] = value;
        });
      }

      if (item.metadata) {
        transformedItem.metadata = item.metadata;
      }

      // Preserve value_metadata for data audit tooltips
      if (item.value_metadata) {
        transformedItem.value_metadata = item.value_metadata;
      }

      if (!transformedItem.name) {
        if (transformedItem.display_name) {
          transformedItem.name = transformedItem.display_name;
        } else if (transformedItem.fund_name) {
          transformedItem.name = transformedItem.fund_name;
        } else if (transformedItem.portfolio_name) {
          transformedItem.name = transformedItem.portfolio_name;
        } else {
          transformedItem.name = `Item ${transformedItem.id}`;
        }
      }

      if (!transformedItem.type) {
        transformedItem.type = transformedItem.resource_type || "product";
      }

      Object.keys(transformedItem).forEach((key) => {
        const value = transformedItem[key];
        if (
          typeof value === "string" &&
          !isNaN(Number(value)) &&
          value.trim() !== ""
        ) {
          transformedItem[key] = Number(value);
        }
      });

      return transformedItem as ScreenerItem;
    });

  return {
    ...response.data,
    items: transformedItems,
  };
};

/**
 * Fetch screener items by their IDs
 * Used to fetch data for pinned items that may not be in the current view
 * @param itemIds - Array of resource IDs to fetch
 * @param columns - Array of column IDs to include
 * @param sortBy - Column to sort by
 * @param sortOrder - Sort direction
 * @param additionalFilters - Optional additional filters to apply (in API format)
 * @param showBenchmarks - Whether to include benchmark resources in the results (default: true for ID lookups)
 */
export const fetchScreenerItemsByIds = async (
  itemIds: string[],
  columns: string[] = [],
  sortBy?: string,
  sortOrder?: "asc" | "desc",
  additionalFilters?: Array<{
    column_id: string;
    operation: string;
    value: any;
  }>,
  showBenchmarks: boolean = true // Default to true since we're looking up specific IDs
): Promise<ScreenerItem[]> => {
  if (!itemIds || itemIds.length === 0) {
    return [];
  }

  try {
    // Start with the base filter for resource_id
    const baseFilters: Array<{
      column_id: string;
      operation: string;
      value: any;
    }> = [
      {
        column_id: "resource_id",
        operation: "in",
        value: itemIds,
      },
    ];

    // Merge with additional filters if provided
    const allFilters = additionalFilters
      ? [...baseFilters, ...additionalFilters]
      : baseFilters;

    const params: ScreenerSearchParams = {
      columns: columns,
      filters: allFilters,
      sort_by: sortBy,
      sort_order: sortOrder,
      page: 1,
      page_size: itemIds.length, // Only need as many as we're looking for
      // Include benchmarks by default when looking up specific IDs
      // This ensures benchmark resources are returned when explicitly requested
      show_benchmarks: showBenchmarks,
    };

    const response = await api.post<ScreenerSearchResponse>(
      "/screener/search",
      params
    );

    // Transform the response data to match the frontend ScreenerItem format
    const transformedItems = response.data.items.map((item: any) => {
      const metaResourceId = item?.metadata?.resource_id;
      const baseId = metaResourceId || item?.id || item?.data?.id;

      const transformedItem: any = {
        id: baseId || `item-${Math.random().toString(36).substr(2, 9)}`,
        resource_id:
          metaResourceId || item?.resource_id || item?.data?.id || item?.id,
        resource_type: item.resource_type,
        display_name: item.display_name || item?.data?.display_name,
        default_stream_type: item.default_stream_type,
        styling: item.styling,
        is_benchmark_row: item.is_benchmark_row,
      };

      if (item.data && typeof item.data === "object") {
        Object.entries(item.data).forEach(([key, value]) => {
          transformedItem[key] = value;
        });
      }

      if (item.metadata) {
        transformedItem.metadata = item.metadata;
      }

      if (item.value_metadata) {
        transformedItem.value_metadata = item.value_metadata;
      }

      if (!transformedItem.name) {
        if (transformedItem.display_name) {
          transformedItem.name = transformedItem.display_name;
        } else if (transformedItem.fund_name) {
          transformedItem.name = transformedItem.fund_name;
        } else if (transformedItem.portfolio_name) {
          transformedItem.name = transformedItem.portfolio_name;
        } else {
          transformedItem.name = `Item ${transformedItem.id}`;
        }
      }

      if (!transformedItem.type) {
        transformedItem.type = transformedItem.resource_type || "product";
      }

      Object.keys(transformedItem).forEach((key) => {
        const value = transformedItem[key];
        if (
          typeof value === "string" &&
          !isNaN(Number(value)) &&
          value.trim() !== ""
        ) {
          transformedItem[key] = Number(value);
        }
      });

      return transformedItem as ScreenerItem;
    });

    return transformedItems;
  } catch (error) {
    console.error("Error fetching screener items by IDs:", error);
    // Return empty array on error to allow graceful degradation
    return [];
  }
};

/**
 * Fetch data for a specific watchlist
 */
export const fetchWatchlistData = async (
  watchlistId: string,
  isDynamic: boolean,
  savedFilters: Filter[],
  activeFilters: Filter[],
  sortBy: string = "name",
  sortOrder: "asc" | "desc" = "asc",
  page: number = 1,
  pageSize: number = 50,
  columns: string[] = [],
  searchTerm?: string
): Promise<ScreenerSearchResponse> => {
  try {
    // Use exactly the columns passed in - these should only be column IDs from the API
    console.log("Fetching watchlist data with column IDs:", columns);
    console.log(`Watchlist sorting by: ${sortBy}, order: ${sortOrder}`);

    // For dynamic watchlists, combine saved filters with active filters
    // For static watchlists, we'll need to fetch the items by their IDs
    if (isDynamic) {
      // Combine saved filters with active filters
      const combinedFilters = [...savedFilters, ...activeFilters];
      // Pass the sort parameters to ensure consistent sorting
      return fetchScreenerData(
        combinedFilters,
        sortBy,
        sortOrder,
        page,
        pageSize,
        columns,
        searchTerm
      );
    } else {
      // For static watchlists, use the watchlist_id parameter instead of a filter
      return fetchScreenerData(
        activeFilters,
        sortBy,
        sortOrder,
        page,
        pageSize,
        columns,
        searchTerm,
        watchlistId
      );
    }
  } catch (error) {
    console.error("Error fetching watchlist data:", error);
    throw error;
  }
};

/**
 * Interface for API views
 */
export interface ScreenerView {
  id: string;
  name: string;
  description: string;
  columns: string[];
  column_order: Record<string, any>;
  created_by: string;
  is_public: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Fetch available views for the screener
 */
export const fetchScreenerViews = async (): Promise<ScreenerView[]> => {
  try {
    // Remove column debug logs

    const response = await api.get<any>("/screener/views");

    if (!response || response.status !== 200) {
      console.error(`API error: ${response?.status} ${response?.statusText}`);
      return [];
    }

    let views: ScreenerView[] = [];

    // Check if the response is an array or an object with a 'data' property
    if (Array.isArray(response.data)) {
      views = response.data;
    } else if (
      response.data &&
      typeof response.data === "object" &&
      Array.isArray(response.data.data)
    ) {
      views = response.data.data;
    } else {
      console.error("Unexpected API response format for views:", response.data);
      return [];
    }

    return views;
  } catch (error) {
    console.error("Error fetching screener views:", error);
    return [];
  }
};

/**
 * Fetch a specific screener view by ID
 * @param viewId The ID of the view to fetch
 * @returns The screener view or null if not found
 */
export const fetchScreenerViewById = async (
  viewId: string
): Promise<ScreenerView | null> => {
  try {
    const response = await api.get<any>(`/screener/views/${viewId}`);

    if (!response || response.status !== 200) {
      console.error(`API error: ${response?.status} ${response?.statusText}`);
      return null;
    }

    // Handle both wrapped and unwrapped response formats
    if (response.data) {
      // If response.data has a nested data property, use that
      if (response.data.data && typeof response.data.data === "object") {
        return response.data.data as ScreenerView;
      }
      // Otherwise, response.data is the view itself
      return response.data as ScreenerView;
    }

    return null;
  } catch (error: unknown) {
    const status = (error as { response?: { status?: number } })?.response?.status;
    if (status !== 404) {
      console.error(`Error fetching screener view ${viewId}:`, error);
    }
    return null;
  }
};

// Interface for saving a new view
export interface SaveScreenerViewParams {
  id?: string;
  name: string;
  description?: string;
  columns: string[];
  is_public?: boolean;
}

/**
 * Save a screener view to the server
 * @param viewData The view data to save
 * @returns The saved view
 */
export async function saveScreenerView(
  viewData: SaveScreenerViewParams
): Promise<ScreenerView> {
  try {
    // Keep minimal logging without excessive details

    // Prepare payload with defaults
    const payload = {
      ...viewData,
      is_public: viewData.is_public ?? false,
    };

    // Use the api client to create the view
    const response = await api.post<ScreenerView>("/screener/views", payload);

    return response.data;
  } catch (error) {
    console.error("Error saving screener view:", error);
    throw error;
  }
}

/**
 * Update an existing screener view on the server
 * @param viewId The ID of the view to update
 * @param viewData The updated view data
 * @returns The updated view
 */
export async function updateScreenerView(
  viewId: string,
  viewData: Omit<SaveScreenerViewParams, "id">
): Promise<ScreenerView> {
  try {
    // Keep minimal logging without excessive details

    // Prepare payload with the ID included (similar to updateFilterGroup)
    const payload = {
      id: viewId,
      ...viewData,
      is_public: viewData.is_public ?? false,
    };

    // Use POST instead of PUT (similar to updateFilterGroup)
    const response = await api.post<ScreenerView>(`/screener/views`, payload);

    return response.data;
  } catch (error) {
    console.error(`Error updating screener view ${viewId}:`, error);
    throw error;
  }
}

/**
 * Delete a screener view from the server
 * @param viewId The ID of the view to delete
 * @returns True if deletion was successful
 */
export async function deleteScreenerView(viewId: string): Promise<boolean> {
  try {
    console.log(`Deleting screener view ${viewId} from server`);

    // Use the api client to delete the view
    const response = await api.delete(`/screener/views/${viewId}`);

    console.log(`Successfully deleted view ${viewId}`);
    return true;
  } catch (error) {
    console.error(`Error deleting screener view ${viewId}:`, error);
    throw error;
  }
}

// Interface for API Screener Filter
export interface ScreenerFilterGroup {
  id: string;
  name: string;
  description?: string;
  created_by?: "system" | "user";
  filters: Array<{
    id: string;
    column: string;
    /** API alias — identical to `column`. Present when the filter comes directly from the API. */
    column_id?: string;
    type: string;
    /** API alias — identical to `type`. Present when the filter comes directly from the API. */
    operation?: string;
    value: unknown;
    displayValue?: string;
  }>;
  filter_logic?: FilterLogic;
  filter_expression?: FilterExpressionPayload;
  sort_by?: string;
  sort_order?: "asc" | "desc";
  default_view_id?: string;
  icon?: string;
  color?: string;
  is_favourite?: boolean;
  isFavourite?: boolean;
}

/**
 * Fetch predefined filter groups for the screener
 */
export async function fetchScreenerFilters(): Promise<ScreenerFilterGroup[]> {
  try {
    const response = await api.get("/screener/filters");

    if (!response.data) {
      throw new Error(
        `Failed to fetch screener filter groups: No data returned`
      );
    }

    // Transform is_favourite (API) to isFavourite (internal) for each filter group
    // Handle both string and boolean values from BE
    const toBool = (val: any): boolean | undefined => {
      if (val === true || val === "true") return true;
      if (val === false || val === "false") return false;
      return undefined;
    };
    const filterGroups = response.data.map((group: any) => ({
      ...group,
      isFavourite: toBool(group.is_favourite) ?? toBool(group.isFavourite),
    }));

    return filterGroups;
  } catch (error) {
    console.error("Error fetching screener filter groups:", error);
    return [];
  }
}

/**
 * Interface for saving a new filter group
 */
export interface SaveFilterGroupParams {
  id?: string;
  name: string;
  description?: string;
  filters: Array<{
    column_id: string;
    operation: string;
    value: any;
    negated?: boolean;
  }>;
  filter_logic?: FilterLogic;
  filter_expression?: FilterExpressionPayload;
  sort_by?: string;
  sort_order?: "asc" | "desc";
  is_public?: boolean;
  created_by?: string;
  icon?: string; // Name of the FontAwesome icon
  color?: string; // Hex color code
  default_view_id?: string;
  is_favourite?: boolean; // API field (snake_case)
  isFavourite?: boolean; // Internal field (camelCase) - for backwards compatibility
}

export async function updateFilterGroup(
  filterId: string,
  filterGroupData: SaveFilterGroupParams,
): Promise<ScreenerFilterGroup> {
  const { isFavourite, ...rest } = filterGroupData;
  const apiPayload = {
    ...rest,
    id: filterId,
    is_favourite: filterGroupData.is_favourite ?? isFavourite,
  };
  const response = await api.put<ScreenerFilterGroup>(`/screener/filters/${filterId}`, apiPayload);
  if (!response?.data) {
    throw new Error("API returned empty response");
  }
  return response.data;
}

export async function fetchScreenerFilterMetadata() {
  const response = await api.get("/screener/filter-metadata");
  return response.data;
}

/**
 * Save a filter group to the server
 * @param filterGroupData The filter group data to save
 * @returns The saved filter group
 */
export async function saveFilterGroup(
  filterGroupData: SaveFilterGroupParams
): Promise<ScreenerFilterGroup> {
  try {
    // Transform isFavourite to is_favourite for API
    const { isFavourite, ...rest } = filterGroupData;
    const apiPayload = {
      ...rest,
      is_favourite: filterGroupData.is_favourite ?? isFavourite,
    };

    const response = await api.post<ScreenerFilterGroup>(
      "/screener/filters",
      apiPayload
    );

    if (!response || !response.data) {
      throw new Error("API returned empty response");
    }

    // Helper to convert string "true"/"false" to boolean
    const toBool = (val: any): boolean | undefined => {
      if (val === true || val === "true") return true;
      if (val === false || val === "false") return false;
      return undefined;
    };

    const safeResponse: ScreenerFilterGroup = {
      id:
        response.data.id ||
        filterGroupData.id ||
        `generated-${Math.random().toString(36).substring(2, 9)}`,
      name: response.data.name || filterGroupData.name,
      description: response.data.description || filterGroupData.description,
      filters: Array.isArray(response.data.filters)
        ? response.data.filters
        : filterGroupData.filters.map((f) => ({
          id: `api-filter-${Math.random().toString(36).substring(2, 9)}`,
          column: f.column_id,
          type: f.operation,
          value: f.value,
        })),
      icon: response.data.icon || filterGroupData.icon,
      color: response.data.color || filterGroupData.color,
      // Transform is_favourite back to isFavourite for internal use
      // Handle both string and boolean values from BE
      isFavourite:
        toBool(response.data.is_favourite) ??
        toBool(response.data.isFavourite) ??
        isFavourite,
    };

    return safeResponse;
  } catch (error) {
    console.error("Error saving filter group:", error);
    throw error;
  }
}

/**
 * Delete a filter group by ID
 * @param filterGroupId The ID of the filter group to delete
 * @returns A boolean indicating whether the deletion was successful
 */
export async function deleteFilterGroup(
  filterGroupId: string
): Promise<boolean> {
  try {
    const response = await api.delete(`/screener/filters/${filterGroupId}`);

    if (!response || response.status !== 200) {
      throw new Error(`Failed to delete filter group: ${response?.statusText}`);
    }

    return true;
  } catch (error) {
    console.error(
      `Error deleting filter group with ID ${filterGroupId}:`,
      error
    );
    throw error;
  }
}

/** A single item stored in a watchlist, including its display position */
export interface WatchlistItem {
  resource_type: string;
  resource_id: string;
  position: number;
}

/**
 * Interface for Watchlist
 */
export interface Watchlist {
  id: string;
  name: string;
  description?: string;
  is_public?: boolean;
  created_at?: string;
  updated_at?: string;
  item_count?: number;
  created_by?: "system" | "user";
  icon?: string; // Name of the FontAwesome icon
  color?: string; // Hex color code
  is_favourite?: boolean; // API field (snake_case)
  isFavourite?: boolean; // Internal field (camelCase) - for backwards compatibility
}

/** Watchlist including its full ordered items list (returned by GET /screener/watchlists/:id) */
export interface WatchlistDetail extends Watchlist {
  items: WatchlistItem[];
}

/**
 * Fetch a single watchlist by ID, including its full ordered items list
 */
export const fetchWatchlistById = async (
  watchlistId: string
): Promise<WatchlistDetail | null> => {
  try {
    const response = await api.get<WatchlistDetail>(
      `/screener/watchlists/${watchlistId}`
    );

    if (!response || response.status !== 200) {
      console.error(`API error: ${response?.status} ${response?.statusText}`);
      return null;
    }

    return response.data ?? null;
  } catch (error) {
    console.error(`Error fetching watchlist ${watchlistId}:`, error);
    return null;
  }
};

/**
 * Fetch available watchlists for the screener
 */
export const fetchScreenerWatchlists = async (): Promise<Watchlist[]> => {
  try {
    const response = await api.get<any>("/screener/watchlists");

    if (!response || response.status !== 200) {
      console.error(`API error: ${response?.status} ${response?.statusText}`);
      return [];
    }

    let watchlists: Watchlist[] = [];

    // Check if the response is an array or an object with a 'data' property
    if (Array.isArray(response.data)) {
      watchlists = response.data;
    } else if (
      response.data &&
      typeof response.data === "object" &&
      Array.isArray(response.data.data)
    ) {
      watchlists = response.data.data;
    } else {
      console.error(
        "Unexpected API response format for watchlists:",
        response.data
      );
      return [];
    }

    // Transform is_favourite (API) to isFavourite (internal) for each watchlist
    // Handle both string and boolean values from BE
    const toBool = (val: any): boolean | undefined => {
      if (val === true || val === "true") return true;
      if (val === false || val === "false") return false;
      return undefined;
    };
    watchlists = watchlists.map((wl: any) => ({
      ...wl,
      isFavourite: toBool(wl.is_favourite) ?? toBool(wl.isFavourite),
    }));

    return watchlists;
  } catch {
    return [];
  }
};

/**
 * Interface for creating or updating a watchlist
 */
export interface SaveWatchlistParams {
  id?: string;
  watchlist_id?: string;
  name: string;
  description?: string;
  items: WatchlistItem[];
  created_by?: string;
  is_public?: boolean;
  icon?: string; // Name of the FontAwesome icon
  color?: string; // Hex color code
  is_favourite?: boolean; // API field (snake_case)
  isFavourite?: boolean; // Internal field (camelCase) - for backwards compatibility
}

/**
 * Save a new watchlist or update an existing one
 */
export async function saveWatchlist(
  watchlistData: SaveWatchlistParams
): Promise<Watchlist> {
  // Transform isFavourite to is_favourite for API
  const { isFavourite, ...rest } = watchlistData;
  const apiPayload = {
    ...rest,
    is_favourite: watchlistData.is_favourite ?? isFavourite,
  };

  const response = await api.post<Watchlist>(
    "/screener/watchlists",
    apiPayload
  );

  if (!response || response.status !== 200) {
    throw new Error(`Failed to save watchlist: ${response?.statusText}`);
  }

  // Transform is_favourite back to isFavourite for internal use
  // Handle both string and boolean values from BE
  const toBool = (val: any): boolean | undefined => {
    if (val === true || val === "true") return true;
    if (val === false || val === "false") return false;
    return undefined;
  };
  return {
    ...response.data,
    isFavourite:
      toBool(response.data.is_favourite) ?? toBool(response.data.isFavourite),
  };
}

/**
 * Delete a watchlist by ID
 */
export async function deleteWatchlist(watchlistId: string): Promise<void> {
  const response = await api.delete(`/screener/watchlists/${watchlistId}`);

  if (!response || response.status !== 200) {
    throw new Error(`Failed to delete watchlist: ${response?.statusText}`);
  }
}

/**
 * Remove a single item from a watchlist
 */
export async function removeItemFromWatchlist(
  watchlistId: string,
  resourceId: string
): Promise<void> {
  const response = await api.delete(
    `/screener/watchlists/${watchlistId}/items/${resourceId}`
  );

  if (!response || response.status !== 200) {
    throw new Error(
      `Failed to remove item from watchlist: ${response?.statusText}`
    );
  }
}

/**
 * Remove multiple items from a watchlist
 */
export async function removeItemsFromWatchlist(
  watchlistId: string,
  resourceIds: string[]
): Promise<void> {
  const response = await api.delete(
    `/screener/watchlists/${watchlistId}/items/bulk`,
    { data: { resource_ids: resourceIds } }
  );

  if (!response || response.status !== 200) {
    throw new Error(
      `Failed to remove items from watchlist: ${response?.statusText}`
    );
  }
}

/**
 * Interface for Resource Metadata response
 */
export interface ResourceMetadata {
  resource_type: string;
  resource_id: string;
  stream_type: string;
  resource_display_name: string;
  benchmark_resource_type?: string;
  benchmark_resource_id?: string;
  benchmark_stream_type?: string;
  benchmark_display_name?: string;
  benchmark_name?: string;
  secondary_benchmark_resource_type?: string;
  secondary_benchmark_resource_id?: string;
  secondary_benchmark_stream_type?: string;
  secondary_benchmark_display_name?: string;
  secondary_benchmark_name?: string;
}

/**
 * Fetch metadata for a specific resource
 * @param resourceType The type of resource (e.g., 'product', 'portfolio')
 * @param resourceId The ID of the resource
 * @returns The resource metadata
 */
export async function fetchResourceMetadata(
  resourceType: string,
  resourceId: string
): Promise<ResourceMetadata> {
  try {
    const response = await api.get<ResourceMetadata>(
      `/screener/resource-metadata/${resourceType}/${resourceId}`
    );

    if (!response || response.status !== 200) {
      throw new Error(
        `Failed to fetch resource metadata: ${response?.statusText}`
      );
    }

    return response.data;
  } catch (error) {
    console.error(
      `Error fetching resource metadata for ${resourceType}/${resourceId}:`,
      error
    );
    throw error;
  }
}

/**
 * Interface for combined list item (filter or watchlist)
 */
export interface CombinedListItem {
  id: string;
  name: string;
  description?: string;
  type: "filter" | "watchlist";
  icon?: string;
  color?: string;
  default_view_id?: string;
  created_by?: string;
  filter_data?: ScreenerFilterGroup;
  watchlist_data?: Watchlist;
  item_count?: number;
  is_favourite?: boolean;
  isFavourite?: boolean;
}

/** Normalize GET /screener/lists payloads (legacy array or wrapped object). */
export function parseCombinedListsResponse(data: unknown): CombinedListsFetchResult {
  if (Array.isArray(data)) {
    return { lists: data as CombinedListItem[], favouriteOrder: [] };
  }
  if (data && typeof data === "object") {
    const payload = data as { lists?: CombinedListItem[]; favourite_order?: string[] };
    return {
      lists: payload.lists ?? [],
      favouriteOrder: payload.favourite_order ?? [],
    };
  }
  return { lists: [], favouriteOrder: [] };
}

/**
 * Fetch combined filters and watchlists
 */
export interface CombinedListsFetchResult {
  lists: CombinedListItem[];
  favouriteOrder: string[];
}

export async function fetchCombinedLists(): Promise<CombinedListsFetchResult> {
  try {
    const response = await api.get("/screener/lists");

    if (!response || response.status !== 200) {
      console.error(`API error: ${response?.status} ${response?.statusText}`);
      return { lists: [], favouriteOrder: [] };
    }

    const { lists: rawLists, favouriteOrder } = parseCombinedListsResponse(response.data);

    // Helper to convert string "true"/"false" to boolean (BE may return strings)
    const toBool = (val: any): boolean | undefined => {
      if (val === true || val === "true") return true;
      if (val === false || val === "false") return false;
      return undefined;
    };

    // Transform is_favourite to isFavourite at top level only
    // Per spec: is_favourite appears only at top level, not in nested filter_data/watchlist_data
    const transformedData = (rawLists || []).map((item: any) => {
      // Check both snake_case and camelCase at top level
      const topLevelFav = toBool(item.is_favourite) ?? toBool(item.isFavourite);

      return {
        ...item,
        // Set both formats at top level for consistent access
        is_favourite: topLevelFav,
        isFavourite: topLevelFav,
        // Pass through nested data without adding is_favourite
        filter_data: item.filter_data,
        watchlist_data: item.watchlist_data,
      };
    });

    return { lists: transformedData, favouriteOrder };
  } catch (error) {
    console.error("Error fetching combined lists:", error);
    return { lists: [], favouriteOrder: [] };
  }
}

/**
 * Update the order of combined lists
 */
export async function updateListOrder(order: string[]): Promise<boolean> {
  try {
    const response = await api.put("/screener/lists/order", order);

    if (!response || response.status !== 200) {
      console.error(`API error: ${response?.status} ${response?.statusText}`);
      return false;
    }

    return true;
  } catch (error) {
    console.error("Error updating list order:", error);
    return false;
  }
}

/**
 * Update the display order of favourite lists (both watchlists and filters)
 * Order is stored centrally in company preferences
 * @param order - Array of prefixed list IDs (e.g., "watchlist-uuid-123" or "filter-uuid-456")
 */
export async function updateFavouriteOrder(order: string[]): Promise<boolean> {
  try {
    const response = await api.put("/screener/lists/favourite-order", order);

    if (!response || response.status !== 200) {
      console.error(`API error: ${response?.status} ${response?.statusText}`);
      return false;
    }

    return true;
  } catch (error) {
    console.error("Error updating favourite order:", error);
    return false;
  }
}

export interface ScreenerFilterFavouriteApi {
  id: string;
  name: string;
  description?: string;
  filters?: Array<{
    column_id: string;
    operation: string;
    value: unknown;
    negated?: boolean;
  }>;
  filter_logic?: FilterLogic;
  filter_expression?: FilterExpressionPayload;
  created_by?: string;
  client_id?: string;
  created_at?: string;
  updated_at?: string;
}

export type CreateFilterFavouriteParams = Omit<
  ScreenerFilterFavouriteApi,
  "id" | "created_by" | "client_id" | "created_at" | "updated_at"
> & { id?: string };

export async function fetchFilterFavourites(): Promise<ScreenerFilterFavouriteApi[]> {
  const response = await api.get<ScreenerFilterFavouriteApi[]>(
    "/screener/filter-favourites",
  );
  return response.data ?? [];
}

export async function createFilterFavourite(
  params: CreateFilterFavouriteParams,
): Promise<ScreenerFilterFavouriteApi> {
  const response = await api.post<ScreenerFilterFavouriteApi>(
    "/screener/filter-favourites",
    params,
  );
  if (!response?.data) {
    throw new Error("API returned empty response");
  }
  return response.data;
}

export async function deleteFilterFavourite(favouriteId: string): Promise<void> {
  const response = await api.delete(
    `/screener/filter-favourites/${favouriteId}`,
  );
  if (!response || response.status !== 200) {
    throw new Error(`Failed to delete filter favourite: ${response?.statusText}`);
  }
}

export type FilterSuggestionReason =
  | "ok"
  | "insufficient_history"
  | "llm_unavailable"
  | "all_dropped";

export type FilterSuggestionCacheScope = "view" | "generated";

export interface FilterSuggestionActiveFilter {
  type: "condition";
  column_id: string;
  operation: string;
  value: unknown;
  negated?: boolean;
}

export interface FilterSuggestionContext {
  view_id?: string | null;
  list_id?: string | null;
  view_column_ids?: string[];
  active_filters?: FilterSuggestionActiveFilter[];
  force_refresh?: boolean;
}

export interface FilterSuggestionItem {
  id: string;
  label: string;
  rationale: string;
  column_id: string;
  operation: string;
  value: unknown;
  inspired_by?: "user" | "company" | "view" | null;
}

export interface FilterSuggestionsResponse {
  suggestions: FilterSuggestionItem[];
  generated_at: string;
  model: string;
  aggregations_cached: boolean;
  reason: FilterSuggestionReason;
  cached: boolean;
  cache_scope: FilterSuggestionCacheScope;
}

export async function fetchFilterSuggestions(
  context: FilterSuggestionContext,
  options?: { forceRefresh?: boolean },
): Promise<FilterSuggestionsResponse> {
  const response = await api.post<FilterSuggestionsResponse>(
    "/screener/filter-suggestions",
    {
      ...context,
      force_refresh: options?.forceRefresh ?? context.force_refresh ?? false,
    },
  );
  return response.data;
}
